<template>
  <ReportShell
    :title="title"
    :stats="stats"
    :exporting="exporting"
    @filter="reload"
    @clear="clearFilters"
    @export="exportExcel"
  >
    <template slot="filters">
      <a-col :xs="24" :sm="12" :md="8" :lg="5">
        <ReportPeriodPicker v-model="period" @change="reload" />
      </a-col>
      <a-col :xs="24" :sm="12" :md="8" :lg="4">
        <a-select
          v-model="groupBy"
          option-label-prop="label"
          :dropdown-style="{ minWidth: '230px' }"
          @change="changeGrouping"
        >
          <a-select-option
            v-for="option in GROUPINGS"
            :key="option.value"
            :value="option.value"
            :label="`Agrupar por ${option.label}`"
          >
            {{ option.label }}
          </a-select-option>
        </a-select>
      </a-col>
      <a-col :xs="24" :sm="12" :md="8" :lg="3">
        <a-select
          v-model="filters.stores"
          mode="multiple"
          placeholder="Requisitante"
          class="fixed-height-select"
          :disabled="options.store_locked"
          :filter-option="filterOption"
          :max-tag-count="1"
          :dropdown-style="{ minWidth: '250px' }"
          @change="reload"
        >
          <a-select-option v-for="store in options.stores" :key="store.id" :value="store.id">
            {{ store.name }}
          </a-select-option>
        </a-select>
      </a-col>
      <a-col :xs="24" :sm="12" :md="8" :lg="3">
        <a-select
          v-model="filters.supplier"
          show-search
          allow-clear
          placeholder="Fornecedor"
          :filter-option="filterOption"
          :dropdown-style="{ minWidth: '300px' }"
          @change="reload"
        >
          <a-select-option v-for="supplier in options.suppliers" :key="supplier.id" :value="supplier.id">
            {{ supplier.name }}
          </a-select-option>
        </a-select>
      </a-col>
      <a-col :xs="24" :sm="12" :md="8" :lg="3">
        <a-select
          v-model="filters.category"
          show-search
          allow-clear
          placeholder="Categoria"
          :filter-option="filterOption"
          :dropdown-style="{ minWidth: '250px' }"
          @change="reload"
        >
          <a-select-option v-for="category in options.categories" :key="category.id" :value="category.id">
            {{ category.name }}
          </a-select-option>
        </a-select>
      </a-col>
      <a-col :xs="24" :sm="12" :md="8" :lg="3">
        <a-input v-model="filters.search" placeholder="Produto" allow-clear @pressEnter="reload" />
      </a-col>
    </template>

    <template slot="drawer">
      <a-col :xs="24" :md="12">
        <a-form-item label="Comprador">
          <a-select
            v-model="filters.buyer"
            show-search
            allow-clear
            placeholder="Todos"
            :filter-option="filterOption"
          >
            <a-select-option v-for="buyer in options.buyers" :key="buyer.id" :value="buyer.id">
              {{ buyer.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-col>
      <a-col :xs="24" :md="12">
        <a-form-item :label="$store.state.identifierText">
          <a-input v-model="filters.identifier" placeholder="Digite" allow-clear @pressEnter="reload" />
        </a-form-item>
      </a-col>
    </template>

    <p class="print-only purchases-report__print-title">
      Compras por {{ groupingLabel }} — {{ periodLabel }}
    </p>

    <!-- scroll do a-table não convive com expandedRowRender: o container do ReportShell rola -->
    <a-table
      :key="dimensions.join('_')"
      size="small"
      row-key="key"
      :columns="columns"
      :data-source="rows"
      :loading="loading"
      :pagination="pagination"
      :expanded-row-keys="expandedKeys"
      :expand-row-by-click="canDrill"
      :expand-icon-as-cell="canDrill"
      :expand-icon="canDrill ? undefined : noExpandIcon"
      :locale="{ emptyText: 'Nenhuma compra no período.' }"
      @expand="toggleRow"
      @change="tableChange"
    >
      <div
        v-if="canDrill"
        slot="expandedRowRender"
        slot-scope="record"
        class="purchases-report__drill"
      >
        <template v-if="drillable(record)">
          <div class="purchases-report__drill-title">
            {{ drillTitle(record) }}
            <span v-if="drills[record.key] && drills[record.key].total > drills[record.key].rows.length">
              — mostrando {{ drills[record.key].rows.length }} de {{ drills[record.key].total }}
            </span>
          </div>
          <a-table
            size="small"
            row-key="key"
            :columns="drillColumns(record)"
            :data-source="drills[record.key] ? drills[record.key].rows : []"
            :loading="!drills[record.key] || drills[record.key].loading"
            :pagination="false"
            :scroll="{ x: 'max-content' }"
          />
        </template>
        <div v-else class="purchases-report__drill-title">Sem detalhamento para esta linha.</div>
      </div>
    </a-table>
  </ReportShell>
</template>

<script>
import moment from "moment";
import PurchaseReportService from "@/services/v3/reports/purchase-report.service";
import reportFormat from "./report-format.mixin";
import ReportPeriodPicker from "../components/ReportPeriodPicker.vue";
import ReportShell from "../components/ReportShell.vue";
import { currentMonth, periodParams } from "../components/report-period";

/**
 * Aba "Compras" do Relatórios: um relatório só com "Agrupar por" no lugar das
 * antigas abas Produtos, Categorias, Fornecedores, Requisitantes e Comprador.
 * O detalhamento de uma linha (antes uma chamada própria por aba) é o mesmo
 * endpoint com um filtro a mais.
 */
const GROUPINGS = [
  { value: "product", label: "Produto" },
  { value: "category", label: "Categoria" },
  { value: "supplier", label: "Fornecedor" },
  { value: "store", label: "Requisitante" },
  { value: "buyer", label: "Comprador" },
  { value: "month", label: "Mês" },
  { value: "request", label: "Cotação" },
  { value: "category_product", label: "Categoria › Produto" },
  { value: "supplier_product", label: "Fornecedor › Produto" },
  { value: "store_product", label: "Requisitante › Produto" },
  { value: "buyer_store", label: "Comprador › Requisitante" },
  { value: "month_supplier", label: "Mês › Fornecedor" },
];

const DIMENSION_LABELS = {
  product: "Produto",
  category: "Categoria",
  supplier: "Fornecedor",
  store: "Requisitante",
  buyer: "Comprador",
  month: "Mês",
  request: "Cotação",
  order: "Pedido",
};

/** Ao abrir uma linha: em que agrupar e por qual filtro prender. */
const DRILLS = {
  product: { groupBy: "order", param: "product_id", title: "Compras do produto" },
  category: { groupBy: "product", param: "category_id", title: "Produtos da categoria" },
  supplier: { groupBy: "product", param: "supplier_id", title: "Produtos do fornecedor" },
  store: { groupBy: "product", param: "store_id", title: "Produtos do requisitante" },
  buyer: { groupBy: "store", param: "buyer_id", title: "Requisitantes do comprador" },
  month: { groupBy: "product", param: "month", title: "Produtos do mês" },
  request: { groupBy: "product", param: "request_id", title: "Produtos da cotação" },
};

/** Cabeçalho do total do pai nos agrupamentos compostos. */
const PARENT_TOTAL_LABELS = {
  category: "Total da categoria",
  supplier: "Total do fornecedor",
  store: "Total do requisitante",
  buyer: "Total do comprador",
  month: "Total do mês",
};

const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

const DRILL_LIMIT = 200;

const emptyTotals = () => ({
  value: 0,
  quantity: 0,
  orders: 0,
  requests: 0,
  stores: 0,
  suppliers: 0,
  products: 0,
});

export default {
  name: "TabPurchases",
  components: { ReportPeriodPicker, ReportShell },
  props: {
    title: { type: String, required: true },
  },
  mixins: [reportFormat],
  data() {
    return {
      GROUPINGS,
      service: new PurchaseReportService(),
      groupBy: "product",
      dimensions: ["product"],
      period: currentMonth(),
      filters: this.emptyFilters(),
      options: { stores: [], suppliers: [], categories: [], buyers: [], store_locked: false },
      rows: [],
      totals: emptyTotals(),
      meta: { current_page: 1, per_page: 30, total: 0 },
      perPage: 30,
      sort: { key: null, dir: null },
      loading: false,
      exporting: false,
      expandedKeys: [],
      drills: {},
      requestSeq: 0,
    };
  },
  computed: {
    compound() {
      return this.dimensions.length > 1;
    },
    canDrill() {
      return !this.compound && !!DRILLS[this.dimensions[0]];
    },
    groupingLabel() {
      const option = GROUPINGS.find((g) => g.value === this.groupBy);
      return option ? option.label : "";
    },
    periodLabel() {
      return `${this.formatDate(this.totals.from)} a ${this.formatDate(this.totals.to)}`;
    },
    stats() {
      const t = this.totals;
      return [
        { key: "value", title: "VALOR TOTAL", value: t.value || 0, precision: 2, prefix: "R$", footerText: this.periodLabel, borderColor: "#52c41a" },
        { key: "orders", title: "PEDIDOS", value: t.orders || 0, suffix: "pedidos", footerText: "pedidos gerados", borderColor: "#1890ff" },
        { key: "requests", title: "COTAÇÕES", value: t.requests || 0, suffix: "cotações", footerText: "com pedido no período", borderColor: "#722ed1" },
        { key: "products", title: "PRODUTOS", value: t.products || 0, suffix: "itens", footerText: "produtos comprados", borderColor: "#FF5722" },
        { key: "suppliers", title: "FORNECEDORES", value: t.suppliers || 0, footerText: "fornecedores com pedido", borderColor: "#faad14" },
        { key: "stores", title: "REQUISITANTES", value: t.stores || 0, footerText: "lojas que compraram", borderColor: "#13c2c2" },
      ];
    },
    pagination() {
      return {
        current: this.meta.current_page,
        pageSize: this.perPage,
        total: this.meta.total,
        showSizeChanger: true,
        pageSizeOptions: ["30", "100", "500"],
        showTotal: (total) => `${total} ${total === 1 ? "linha" : "linhas"}`,
      };
    },
    /** Linhas de cada pai no agrupamento composto (rowspan). */
    spans() {
      if (!this.compound) return [];
      const parent = this.dimensions[0];
      const spans = [];
      let start = 0;
      this.rows.forEach((row, index) => {
        const id = row[parent] ? row[parent].id : null;
        const previous = index > 0 && this.rows[index - 1][parent] ? this.rows[index - 1][parent].id : undefined;
        if (index === 0 || id !== previous) {
          start = index;
          spans[index] = 1;
        } else {
          spans[start] += 1;
          spans[index] = 0;
        }
      });
      return spans;
    },
    columns() {
      return this.buildColumns(this.dimensions, { sortable: true });
    },
  },
  mounted() {
    this.loadOptions();
    this.load(1);
  },
  methods: {
    emptyFilters() {
      return { stores: [], supplier: undefined, category: undefined, buyer: undefined, search: "", identifier: "" };
    },
    params(extra = {}) {
      const params = {
        group_by: this.groupBy,
        ...periodParams(this.period),
        store_id: this.filters.stores.length ? this.filters.stores : undefined,
        supplier_id: this.filters.supplier,
        category_id: this.filters.category,
        buyer_id: this.filters.buyer,
        search: this.filters.search || undefined,
        identifier: this.filters.identifier || undefined,
        sort: this.sort.key || undefined,
        dir: this.sort.key ? this.sort.dir : undefined,
      };
      return { ...params, ...extra };
    },
    loadOptions() {
      this.service
        .options()
        .then(({ data }) => {
          this.options = data;
        })
        .catch(() => {});
    },
    reload() {
      this.load(1);
    },
    changeGrouping() {
      this.sort = { key: null, dir: null };
      this.load(1);
    },
    load(page) {
      const seq = ++this.requestSeq;
      this.loading = true;
      this.expandedKeys = [];
      this.drills = {};
      this.service
        .list(this.params({ page, per_page: this.perPage }))
        .then(({ data }) => {
          // uma resposta lenta de um filtro anterior não sobrescreve a atual
          if (seq !== this.requestSeq) return;
          this.rows = data.data;
          this.dimensions = data.dimensions;
          this.totals = data.totals;
          this.meta = data.meta;
        })
        .catch((error) => {
          if (seq !== this.requestSeq) return;
          this.rows = [];
          this.totals = emptyTotals();
          this.meta = { current_page: 1, per_page: this.perPage, total: 0 };
          this.notifyError(error, "Não foi possível carregar o relatório de compras.");
        })
        .finally(() => {
          if (seq === this.requestSeq) this.loading = false;
        });
    },
    tableChange(pagination, _filters, sorter) {
      const key = sorter && sorter.order ? sorter.columnKey : null;
      const dir = sorter && sorter.order === "ascend" ? "asc" : "desc";
      const sortChanged = key !== this.sort.key || (key && dir !== this.sort.dir);
      const sizeChanged = pagination.pageSize !== this.perPage;

      this.sort = { key, dir: key ? dir : null };
      this.perPage = pagination.pageSize;
      this.load(sortChanged || sizeChanged ? 1 : pagination.current);
    },
    clearFilters() {
      this.filters = this.emptyFilters();
      this.sort = { key: null, dir: null };
      this.load(1);
    },
    exportExcel() {
      this.exporting = true;
      this.service
        .export(this.params())
        .then(({ data }) => this.downloadBlob(data, `relatorio-compras-${this.groupBy}.xlsx`))
        .catch((error) => this.notifyError(error, "Não foi possível exportar o relatório de compras."))
        .finally(() => {
          this.exporting = false;
        });
    },

    // ----------------------------------------------------------- detalhamento

    noExpandIcon() {
      return null;
    },
    drillable(record) {
      const dimension = this.dimensions[0];
      return !!DRILLS[dimension] && record[dimension] && record[dimension].id !== null;
    },
    drillTitle(record) {
      return `${DRILLS[this.dimensions[0]].title}: ${this.dimensionName(this.dimensions[0], record[this.dimensions[0]])}`;
    },
    toggleRow(expanded, record) {
      if (!expanded) {
        this.expandedKeys = this.expandedKeys.filter((key) => key !== record.key);
        return;
      }
      this.expandedKeys = [...this.expandedKeys, record.key];
      if (this.drills[record.key] || !this.drillable(record)) return;

      const dimension = this.dimensions[0];
      const drill = DRILLS[dimension];
      const id = record[dimension].id;
      const extra = { group_by: drill.groupBy, page: 1, per_page: DRILL_LIMIT, sort: undefined, dir: undefined };

      if (drill.param === "store_id") {
        extra.store_id = [id];
      } else if (drill.param === "month") {
        Object.assign(extra, this.monthBounds(id));
      } else {
        extra[drill.param] = id;
      }

      this.$set(this.drills, record.key, { loading: true, rows: [], total: 0, dimensions: [drill.groupBy] });
      this.service
        .list(this.params(extra))
        .then(({ data }) => {
          this.$set(this.drills, record.key, {
            loading: false,
            rows: data.data,
            total: data.meta.total,
            dimensions: data.dimensions,
          });
        })
        .catch((error) => {
          this.$set(this.drills, record.key, { loading: false, rows: [], total: 0, dimensions: [drill.groupBy] });
          this.notifyError(error, "Não foi possível carregar o relatório de compras.");
        });
    },
    /** O mês da linha, recortado pelo período escolhido. */
    monthBounds(month) {
      const start = moment(`${month}-01`);
      const end = start.clone().endOf("month");
      const from = this.period[0].clone().startOf("day");
      const to = this.period[1].clone().startOf("day");
      return {
        from: moment.max(start, from).format("YYYY-MM-DD"),
        to: moment.min(end, to).format("YYYY-MM-DD"),
      };
    },
    drillColumns(record) {
      const drill = this.drills[record.key];
      const dimensions = drill ? drill.dimensions : [DRILLS[this.dimensions[0]].groupBy];
      return this.buildColumns(dimensions, { parentValue: record.value });
    },

    // ----------------------------------------------------------------- colunas

    /**
     * Colunas de dimensão + métricas. `sortable` só na tabela principal (a
     * ordenação é do servidor); `parentValue` troca o "%" pelo % do pai.
     */
    buildColumns(dimensions, { sortable = false, parentValue = null } = {}) {
      const compound = dimensions.length > 1;
      const columns = [];

      dimensions.forEach((dimension, position) => {
        const isParent = compound && position === 0;
        const hidden = dimension === "product" && dimensions.includes("category") ? ["product_category"] : [];
        const nameSort = sortable && (!compound || position === 0) ? (dimension === "month" ? "key" : "name") : null;
        this.dimensionColumns(dimension).filter((column) => !hidden.includes(column.key)).forEach((column, index) => {
          const spec = { ...column };
          if (index === 0 && nameSort) {
            Object.assign(spec, this.sortable(nameSort));
          }
          if (isParent) {
            const render = spec.customRender;
            spec.customRender = (text, row, rowIndex) => ({
              children: render(text, row, rowIndex),
              attrs: { rowSpan: this.spans[rowIndex] === undefined ? 1 : this.spans[rowIndex] },
            });
          }
          columns.push(spec);
        });

        if (isParent) {
          columns.push({
            title: PARENT_TOTAL_LABELS[dimension],
            key: "parent_value",
            align: "right",
            customRender: (text, row, rowIndex) => ({
              children: row.parent ? `${this.brl(row.parent.value)} (${this.pct(row.parent.share)})` : "--",
              attrs: { rowSpan: this.spans[rowIndex] === undefined ? 1 : this.spans[rowIndex] },
            }),
          });
        }
      });

      const leaf = dimensions[dimensions.length - 1];
      const metric = (title, key, render, extra = {}) =>
        columns.push({ title, key, align: "right", customRender: (t, row) => render(row), ...(sortable ? this.sortable(key) : {}), ...extra });

      metric("Quantidade", "quantity", (row) => this.num(row.quantity));
      if (leaf !== "order") {
        metric("Pedidos", "orders", (row) => this.int(row.orders));
        metric("Cotações", "requests", (row) => this.int(row.requests));
      }
      if (leaf === "product" || leaf === "order") {
        metric("Preço médio", "average_price", (row) => (row.average_price === null ? "--" : this.brl(row.average_price)));
      }
      metric("Valor total", "value", (row) => this.brl(row.value));

      if (parentValue !== null) {
        columns.push({
          title: "%",
          key: "share_of_parent",
          align: "right",
          customRender: (t, row) => this.pct(parentValue > 0 ? (row.value * 100) / parentValue : 0),
        });
      } else if (compound) {
        columns.push({ title: "% do grupo", key: "share_of_parent", align: "right", customRender: (t, row) => this.pct(row.share_of_parent) });
        columns.push({ title: "% do total", key: "share", align: "right", customRender: (t, row) => this.pct(row.share) });
      } else {
        columns.push({ title: "%", key: "share", align: "right", customRender: (t, row) => this.pct(row.share) });
        columns.push({ title: "% acumulado", key: "cumulative_share", align: "right", customRender: (t, row) => this.pct(row.cumulative_share) });
      }

      return columns;
    },
    sortable(key) {
      return {
        key,
        sorter: true,
        sortOrder: this.sort.key === key ? (this.sort.dir === "asc" ? "ascend" : "descend") : false,
      };
    },
    dimensionColumns(dimension) {
      const of = (row) => row[dimension] || {};
      switch (dimension) {
        case "product":
          return [
            { title: "Produto", key: "product_name", customRender: (t, row) => of(row).name || "--" },
            { title: "Código", key: "product_ean", customRender: (t, row) => of(row).ean || "--" },
            { title: "Categoria", key: "product_category", customRender: (t, row) => of(row).category || "--" },
            { title: "Embalagem", key: "product_packing", customRender: (t, row) => of(row).packing || "--" },
          ];
        case "request":
          return [
            { title: "Cotação", key: "request_id", customRender: (t, row) => `#${of(row).id}` },
            { title: this.$store.state.identifierText, key: "request_identifier", customRender: (t, row) => of(row).identifier || "--" },
            { title: "Data", key: "request_created_at", customRender: (t, row) => this.formatDate(of(row).created_at) },
          ];
        case "order":
          return [
            { title: "Data", key: "order_created_at", customRender: (t, row) => this.formatDate(of(row).created_at) },
            { title: "Pedido", key: "order_id", customRender: (t, row) => `#${of(row).id}` },
            { title: "Cotação", key: "order_request", customRender: (t, row) => (of(row).request_id ? `#${of(row).request_id}` : "--") },
            { title: "Fornecedor", key: "order_supplier", customRender: (t, row) => of(row).supplier || "--" },
            { title: "Requisitante", key: "order_store", customRender: (t, row) => of(row).store || "--" },
            { title: "Marca", key: "order_brand", customRender: (t, row) => of(row).brand || "--" },
          ];
        default:
          return [
            {
              title: DIMENSION_LABELS[dimension],
              key: `${dimension}_name`,
              customRender: (t, row) => this.dimensionName(dimension, of(row)),
            },
          ];
      }
    },
    dimensionName(dimension, value) {
      if (!value) return "--";
      if (dimension === "month") return this.monthLabel(value.id);
      if (dimension === "request") return `#${value.id}`;
      if (dimension === "order") return `#${value.id}`;
      if (value.id === null) return dimension === "buyer" ? "Não identificado" : "Sem requisitante";
      return value.name || "--";
    },

    // -------------------------------------------------------------- formatação

    monthLabel(month) {
      if (!month) return "--";
      const [year, number] = month.split("-");
      return `${MONTHS[Number(number) - 1]}/${year}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.purchases-report__drill {
  padding: 4px 0 8px;
}

.purchases-report__drill-title {
  font-weight: 600;
  color: #605f5f;
  margin-bottom: 8px;
}

.purchases-report__print-title {
  font-weight: 600;
  font-size: 1.1rem;
}

.print-only {
  display: none;
}

@media print {
  .print-only {
    display: block;
  }
}
</style>
